import CloseOutlined from '@ant-design/icons/CloseOutlined';
import Input from 'antd/es/input';
import React, { useState, useEffect } from 'react';
import { styled } from 'styled-components';

import type { PipelineBlock, PipelineConfiguration } from '@mai/types';

import {
  addBlockToPipelineConfig,
  createBlockFromDefinition,
} from '../../utils/block-creation';
import { BlockPalette } from '../BlockPalette';
import type { BlockDefinition } from '../BlockPalette';
import {
  ArtifactBlockEditor,
  ClauseExtractionBlockEditor,
  ConstantBlockEditor,
  DatasetBlockEditor,
  EmbeddingsBlockEditor,
  FileBlockEditor,
  IdentityBlockEditor,
  InputBlockEditor,
  JoinBlockEditor,
  JsonTemplateBlockEditor,
  LegalVarianceDeterminationBlockEditor,
  LLMBlockEditor,
  MapBlockEditor,
  OutputBlockEditor,
  PDFReaderBlockEditor,
  SemanticFilterBlockEditor,
  StringTemplateBlockEditor,
  TextSplitterBlockEditor,
  TransformBlockEditor,
  ZipBlockEditor,
} from './editors';

const Panel = styled.div`
  background: ${({ theme }) => theme.colorBgContainer};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  height: 100%;
  padding: ${({ theme }) => theme.paddingMD}px;
  overflow-y: auto;
  color: ${({ theme }) => theme.colorText};
  border: 1px solid ${({ theme }) => theme.colorBorder};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const BlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.colorBorder};
  padding-bottom: ${({ theme }) => theme.paddingSM}px;
  margin-bottom: ${({ theme }) => theme.marginMD}px;
`;

const BlockTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeightStrong};
  font-size: ${({ theme }) => theme.fontSizeLG}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

const BlockType = styled.div`
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  color: ${({ theme }) => theme.colorTextSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlockInfo = styled.div`
  flex: 1;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colorTextSecondary};
  cursor: pointer;
  padding: 0;
  border-radius: ${({ theme }) => theme.borderRadiusSM}px;
  transition: all ${({ theme }) => theme.motionDurationFast};

  &:hover {
    color: ${({ theme }) => theme.colorText};
    background: ${({ theme }) => theme.colorFillTertiary};
  }
`;

const MetadataSection = styled.div`
  margin-bottom: ${({ theme }) => theme.marginLG}px;
  padding-bottom: ${({ theme }) => theme.paddingMD}px;
  border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.marginMD}px;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize}px;
  color: ${({ theme }) => theme.colorText};
  margin-bottom: ${({ theme }) => theme.marginXS}px;
  font-weight: ${({ theme }) => theme.fontWeightStrong};
`;

const { TextArea } = Input;

// Memoized block editor to prevent unnecessary re-renders
const BlockEditor = React.memo<{
  block: PipelineBlock;
  onConfigChange: (config: PipelineBlock['config']) => void;
  connections?: PipelineConfiguration['connections'];
  allBlocks?: PipelineConfiguration['blocks'];
}>(({ block, onConfigChange, connections, allBlocks }) => {
  switch (block.type) {
    // IO Blocks
    case 'constant':
      return (
        <ConstantBlockEditor block={block} onConfigChange={onConfigChange} />
      );
    case 'input':
      return <InputBlockEditor block={block} onConfigChange={onConfigChange} />;
    case 'output':
      return (
        <OutputBlockEditor block={block} onConfigChange={onConfigChange} />
      );
    case 'dataset':
      return (
        <DatasetBlockEditor block={block} onConfigChange={onConfigChange} />
      );
    case 'file':
      return <FileBlockEditor block={block} onConfigChange={onConfigChange} />;
    case 'artifact':
      return (
        <ArtifactBlockEditor block={block} onConfigChange={onConfigChange} />
      );

    // Flow Blocks
    case 'identity':
      return (
        <IdentityBlockEditor block={block} onConfigChange={onConfigChange} />
      );
    case 'zip':
      return <ZipBlockEditor block={block} onConfigChange={onConfigChange} />;
    case 'map':
      return <MapBlockEditor block={block} onConfigChange={onConfigChange} />;
    case 'join':
      return (
        <JoinBlockEditor
          block={block}
          onConfigChange={onConfigChange}
          connections={connections}
          allBlocks={allBlocks}
        />
      );

    // AI Blocks
    case 'llm':
      return <LLMBlockEditor block={block} onConfigChange={onConfigChange} />;
    case 'embeddings':
      return (
        <EmbeddingsBlockEditor block={block} onConfigChange={onConfigChange} />
      );
    case 'semantic_filter':
      return (
        <SemanticFilterBlockEditor
          block={block}
          onConfigChange={onConfigChange}
        />
      );
    case 'clause_extraction_agent':
      return (
        <ClauseExtractionBlockEditor
          block={block}
          onConfigChange={onConfigChange}
        />
      );
    case 'legal_variance_determination':
      return (
        <LegalVarianceDeterminationBlockEditor
          block={block}
          onConfigChange={onConfigChange}
        />
      );

    // Transform Blocks
    case 'string_template':
      return (
        <StringTemplateBlockEditor
          block={block}
          onConfigChange={onConfigChange}
        />
      );
    case 'text_splitter':
      return (
        <TextSplitterBlockEditor
          block={block}
          onConfigChange={onConfigChange}
        />
      );
    case 'transform':
      return (
        <TransformBlockEditor block={block} onConfigChange={onConfigChange} />
      );
    case 'pdf_reader':
      return (
        <PDFReaderBlockEditor block={block} onConfigChange={onConfigChange} />
      );
    case 'json_template':
      return (
        <JsonTemplateBlockEditor
          block={block}
          onConfigChange={onConfigChange}
        />
      );
  }
  throw new Error(`Unknown block type: ${block}`);
});

interface ContextConfigurationPanelProps {
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
  pipelineConfig?: PipelineConfiguration | null;
  onBlockConfigChange?: (
    blockId: string,
    config: PipelineBlock['config'],
  ) => void;
  onBlockMetadataChange?: (
    blockId: string,
    metadata: { name?: string; description?: string },
  ) => void;
  onPipelineConfigChange?: (config: PipelineConfiguration) => void;
  onNodeDeselect?: () => void;
}

export const ContextConfigurationPanel: React.FC<
  ContextConfigurationPanelProps
> = ({
  selectedNodeId,
  selectedEdgeId,
  pipelineConfig,
  onBlockConfigChange,
  onBlockMetadataChange,
  onPipelineConfigChange,
  onNodeDeselect,
}) => {
  // Local state for name and description
  const [blockName, setBlockName] = useState<string>('');
  const [blockDescription, setBlockDescription] = useState<string>('');
  
  // Find the selected block in the pipeline configuration
  const selectedBlock = React.useMemo(() => {
    if (!selectedNodeId || !pipelineConfig?.blocks) return null;
    // blocks is now an object with block IDs as keys
    return pipelineConfig.blocks[selectedNodeId] || null;
  }, [selectedNodeId, pipelineConfig]);

  // Sync local state with selected block
  useEffect(() => {
    if (selectedBlock) {
      setBlockName(selectedBlock.name || '');
      setBlockDescription(selectedBlock.description || '');
    }
  }, [selectedBlock]);

  // Find the selected edge in the pipeline configuration
  const selectedConnection = React.useMemo(() => {
    if (!selectedEdgeId || !pipelineConfig?.connections) return null;

    // Parse edge ID to get source and target block IDs
    const [sourceBlockId, targetBlockId] = selectedEdgeId.split('-');

    // Find the connection that matches the edge
    return (
      pipelineConfig.connections.find(
        (conn) =>
          conn.source_block_id === sourceBlockId &&
          conn.target_block_id === targetBlockId,
      ) || null
    );
  }, [selectedEdgeId, pipelineConfig]);

  const handleConfigChange = (config: PipelineBlock['config']) => {
    if (selectedBlock && onBlockConfigChange) {
      onBlockConfigChange(selectedBlock.id, config);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setBlockName(newName);
    if (selectedBlock && onBlockMetadataChange) {
      onBlockMetadataChange(selectedBlock.id, { 
        name: newName || undefined,
        description: blockDescription || undefined,
      });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value;
    setBlockDescription(newDescription);
    if (selectedBlock && onBlockMetadataChange) {
      onBlockMetadataChange(selectedBlock.id, {
        name: blockName || undefined,
        description: newDescription || undefined,
      });
    }
  };

  // Handle block click from palette (for non-drag interactions)
  const handleBlockClick = (blockDefinition: BlockDefinition) => {
    if (!pipelineConfig || !onPipelineConfigChange) {
      return;
    }

    // Create new block at a default position
    const newBlock = createBlockFromDefinition(blockDefinition);

    // Add block to pipeline configuration
    const updatedConfig = addBlockToPipelineConfig(pipelineConfig, newBlock);

    // Update the pipeline configuration
    onPipelineConfigChange(updatedConfig);
  };

  // Show edge configuration when an edge is selected
  if (selectedConnection && selectedEdgeId) {
    const sourceBlock =
      pipelineConfig?.blocks[selectedConnection.source_block_id];
    const targetBlock =
      pipelineConfig?.blocks[selectedConnection.target_block_id];

    return (
      <Panel>
        <BlockHeader>
          <BlockTitle>Connection</BlockTitle>
          <BlockType>Edge Configuration</BlockType>
        </BlockHeader>
        <div style={{ padding: '16px' }}>
          <div style={{ marginBottom: '12px' }}>
            <strong>Source:</strong> {sourceBlock?.type || 'Unknown'} (
            {selectedConnection.source_block_id})
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>Source Port:</strong> {selectedConnection.source_port}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>Target:</strong> {targetBlock?.type || 'Unknown'} (
            {selectedConnection.target_block_id})
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>Target Port:</strong> {selectedConnection.target_port}
          </div>
        </div>
      </Panel>
    );
  }

  // Show block palette when no node is selected
  if (!selectedBlock) {
    return (
      <Panel>
        {selectedNodeId ? (
          <EmptyState>Block not found: {selectedNodeId}</EmptyState>
        ) : (
          <BlockPalette onBlockClick={handleBlockClick} />
        )}
      </Panel>
    );
  }

  return (
    <Panel>
      <BlockHeader>
        <BlockInfo>
          <BlockTitle>{blockName || selectedBlock.id}</BlockTitle>
          <BlockType>{selectedBlock.type} block</BlockType>
        </BlockInfo>
        {onNodeDeselect && (
          <CloseButton onClick={onNodeDeselect} aria-label="Close">
            <CloseOutlined />
          </CloseButton>
        )}
      </BlockHeader>

      {/* Name and Description fields */}
      <MetadataSection>
        <FormGroup>
          <Label htmlFor="block-name">Block Name</Label>
          <Input
            id="block-name"
            value={blockName}
            onChange={handleNameChange}
            placeholder="Enter a custom name for this block"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="block-description">Description</Label>
          <TextArea
            id="block-description"
            value={blockDescription}
            onChange={handleDescriptionChange}
            placeholder="Describe what this block does"
            rows={3}
          />
        </FormGroup>
      </MetadataSection>

      <BlockEditor
        block={selectedBlock}
        onConfigChange={handleConfigChange}
        connections={pipelineConfig?.connections}
        allBlocks={pipelineConfig?.blocks}
      />
    </Panel>
  );
};
